/* eslint-disable react-hooks/exhaustive-deps */
import Separator from "@/components/Common/Separator";
import MobileMenu from "@/components/Header/MobileMenu";
import Cart from "@/components/Header/Offcanvas/Cart";
import BackToTop from "@/pages/backToTop";
import PageHead from "@/pages/Head";
import axios from "axios";
import { useRouter } from "next/router";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaCircle, FaFlag } from "react-icons/fa";
import { generateLabel } from "@/context/utilities";
import ExtraModals from "@/components/Student/question/ExtraModals";
import QuestionNotepad from "@/components/Student/question/QuestionNotepad";
import LoadAndDisplay from "@/context/LoadAndDisplay";
import { connectToDatabase } from "@/components/Server/Database/MongoDB";
import Image from "next/image";

export const openModal = async (id) => {
  const bootstrap = await import("bootstrap");
  const modalElement = document.getElementById(id);

  if (modalElement) {
    let modalInstance = bootstrap.Modal.getInstance(modalElement);

    if (!modalInstance) {
      modalInstance = new bootstrap.Modal(modalElement);
    }

    modalInstance.show();
  }
};

const SubjectSystemSelector = () => {
  const router = useRouter();
  const [data, setData] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState({});
  const [markedQuestions, setMarkedQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [answers, setAnswers] = useState({});
  const [submittedAnswers, setSubmittedAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [unansweredCount, setUnansweredCount] = useState(0);
  const [timeInitialized, setTimeInitialized] = useState(false);

  const [notesStore, setNotesStore] = useState({});
  const [isNotepadOpen, setIsNotepadOpen] = useState(false);

  const endTestRef = useRef(null);

  const selectedQuestionId = selectedQuestion?._id;
  const selectedQuestionLabel =
    selectedQuestion?.index !== undefined
      ? `for Q. ${selectedQuestion.index + 1}`
      : "";
  const notesStorageKey = useMemo(() => {
    if (!router.isReady || !router.query.id) {
      return null;
    }
    return `question-notes:${router.query.id}`;
  }, [router.isReady, router.query.id]);

  const timeStorageKey = useMemo(() => {
    if (!router.isReady || !router.query.id) {
      return null;
    }
    return `test-timer:${router.query.id}`;
  }, [router.isReady, router.query.id]);

  useEffect(() => {
    if (!timeStorageKey) {
      return;
    }
    setTimeInitialized(false);
    setTimeLeft(0);
  }, [timeStorageKey]);

  useEffect(() => {
    const handleMouseLeave = (event) => {
      if (event.clientY <= 0) {
        openModal("tabSwitch");

        // Add your logic here
      }
    };

    const handleMouseMove = (event) => {
      if (event.clientY <= 0) {
        openModal("tabSwitch");
      }
    };

    // Add event listeners
    document.addEventListener("mouseleave", handleMouseLeave);
    document.addEventListener("mousemove", handleMouseMove);

    // Cleanup the event listeners on component unmount
    return () => {
      document.removeEventListener("mouseleave", handleMouseLeave);
      document.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  useEffect(() => {
    // Prevent page reload
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      openModal("preventReload");
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (data.isCompleted) {
      router.push(`/student/question/test/summary/${data.testId}`);
    }
  }, [router, data]);

  useEffect(() => {
    if (!notesStorageKey || typeof window === "undefined") {
      return;
    }

    try {
      const stored = window.localStorage.getItem(notesStorageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === "object") {
          setNotesStore(parsed);
        }
      }
    } catch (error) {
      console.warn("Unable to load stored notes", error);
    }
  }, [notesStorageKey]);

  useEffect(() => {
    if (!notesStorageKey || typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(notesStorageKey, JSON.stringify(notesStore));
    } catch (error) {
      console.warn("Unable to persist notes", error);
    }
  }, [notesStore, notesStorageKey]);

  useEffect(() => {
    if (typeof window === "undefined" || !timeStorageKey || timeInitialized) {
      return;
    }

    const readStoredValue = () => {
      const directValue = window.localStorage.getItem(timeStorageKey);
      if (directValue) {
        const parsed = parseInt(directValue, 10);
        if (!Number.isNaN(parsed) && parsed >= 0) {
          return parsed;
        }
      }

      const legacyValue = window.localStorage.getItem("timeLeft");
      if (legacyValue) {
        const parsed = parseInt(legacyValue, 10);
        if (!Number.isNaN(parsed) && parsed >= 0) {
          window.localStorage.removeItem("timeLeft");
          return parsed;
        }
      }

      return null;
    };

    const storedValue = readStoredValue();
    if (storedValue !== null) {
      window.localStorage.setItem(timeStorageKey, String(storedValue));
      setTimeLeft(storedValue);
      if (storedValue > 0) {
        setTimeInitialized(true);
      }
      return;
    }

    if (data?.duration) {
      const initialSeconds = data.duration * 60;
      window.localStorage.setItem(timeStorageKey, String(initialSeconds));
      setTimeLeft(initialSeconds);
      if (initialSeconds > 0) {
        setTimeInitialized(true);
      }
    }
  }, [data?.duration, timeInitialized, timeStorageKey]);

  useEffect(() => {
    if (typeof window === "undefined" || !timeStorageKey || !timeInitialized) {
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (typeof prevTime !== "number" || Number.isNaN(prevTime)) {
          return prevTime;
        }

        if (prevTime <= 1) {
          window.localStorage.setItem(timeStorageKey, "0");
          clearInterval(timer);
          setTimeInitialized(false);
          if (endTestRef.current) {
            endTestRef.current();
          }
          return 0;
        }

        const nextTime = prevTime - 1;
        window.localStorage.setItem(timeStorageKey, String(nextTime));
        return nextTime;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeInitialized, timeStorageKey]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `/api/question/test?id=${router.query.id}`
        );
        setData(response.data);
        setSelectedQuestion({
          ...response.data.questions[0],
          index: 0,
        });
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (router.query.id) fetchData();
  }, [router.query.id]);

  const createNote = useCallback((questionId) => {
    if (!questionId) return;

    setNotesStore((prev) => {
      const existing = prev[questionId] || [];
      const timestamp = Date.now();
      const newNote = {
        id: `note-${timestamp}-${Math.random().toString(16).slice(2)}`.slice(
          0,
          32
        ),
        text: "",
        createdAt: timestamp,
        updatedAt: timestamp,
      };

      return {
        ...prev,
        [questionId]: [...existing, newNote],
      };
    });
  }, []);

  const updateNote = useCallback((questionId, noteId, text) => {
    if (!questionId || !noteId) return;

    setNotesStore((prev) => {
      const questionNotes = prev[questionId] || [];
      let didUpdate = false;

      const updatedNotes = questionNotes.map((note) => {
        if (note.id !== noteId) {
          return note;
        }

        if (note.text === text) {
          return note;
        }

        didUpdate = true;
        return {
          ...note,
          text,
          updatedAt: Date.now(),
        };
      });

      if (!didUpdate) {
        return prev;
      }

      return {
        ...prev,
        [questionId]: updatedNotes,
      };
    });
  }, []);

  const deleteNote = useCallback((questionId, noteId) => {
    if (!questionId || !noteId) return;

    setNotesStore((prev) => {
      const questionNotes = prev[questionId] || [];
      const updatedNotes = questionNotes.filter((note) => note.id !== noteId);

      if (updatedNotes.length === questionNotes.length) {
        return prev;
      }

      const nextState = { ...prev };
      if (updatedNotes.length === 0) {
        delete nextState[questionId];
      } else {
        nextState[questionId] = updatedNotes;
      }

      return nextState;
    });
  }, []);

  const openNotepad = useCallback(() => {
    if (!selectedQuestionId) return;
    setIsNotepadOpen(true);
  }, [selectedQuestionId]);

  const closeNotepad = useCallback(() => {
    setIsNotepadOpen(false);
  }, []);

  const toggleNotepad = useCallback(() => {
    if (!selectedQuestionId) return;
    setIsNotepadOpen((prev) => !prev);
  }, [selectedQuestionId]);

  const handleMarkQuestion = (questionId) => {
    setMarkedQuestions((prev) =>
      prev.includes(questionId)
        ? prev.filter((id) => id !== questionId)
        : [...prev, questionId]
    );
  };

  const handleNextQuestion = () => {
    const currentIndex = selectedQuestion?.index || 0;
    const nextIndex = (currentIndex + 1) % data?.questions?.length;
    setSelectedQuestion({ ...data?.questions?.[nextIndex], index: nextIndex });
    window.scrollTo({ top: 0 });
  };

  const handlePrevQuestion = () => {
    const currentIndex = selectedQuestion?.index || 0;
    const prevIndex =
      (currentIndex - 1 + data?.questions?.length) % data?.questions?.length;
    setSelectedQuestion({ ...data?.questions?.[prevIndex], index: prevIndex });
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleOptionChange = (questionId, optionIndex) => {
    if (!submittedAnswers[questionId]) {
      setAnswers((prev) => ({
        ...prev,
        [questionId]: optionIndex,
      }));
    }
  };

  const handleSubmitAnswer = (questionId) => {
    if (answers[questionId] !== undefined) {
      setSubmittedAnswers((prev) => ({
        ...prev,
        [questionId]: answers[questionId],
      }));
      handleNextQuestion();
    }
  };

  const handleEndTest = () => {
    const unansweredQuestions = data.questions.filter(
      (question) => !submittedAnswers[question._id]
    );
    setUnansweredCount(unansweredQuestions.length);

    if (unansweredQuestions.length > 0) {
      const bootstrap = require("bootstrap");
      const endTestModal = new bootstrap.Modal(
        document.getElementById("endTestModal")
      );
      endTestModal.show();
    } else {
      endTest();
    }
  };

  const handleConfirmEndTest = async () => {
    const bootstrap = await import("bootstrap");

    // Close the modal after confirmation
    const endTestModal = bootstrap.Modal.getInstance(
      document.getElementById("endTestModal")
    );
    endTestModal.hide();

    await endTest();
  };

  const endTest = useCallback(async () => {
    if (!data?.testId) {
      return;
    }

    try {
      setLoading(true);
      await axios.put("/api/question/test", {
        testId: data.testId,
        answers: submittedAnswers,
        time: timeLeft,
      });

      if (typeof window !== "undefined") {
        if (timeStorageKey) {
          window.localStorage.removeItem(timeStorageKey);
        }
        window.localStorage.removeItem("timeLeft");
      }

      router.push(`/student/question/test/summary/${data.testId}`);
    } catch (error) {
      console.error("Error ending test:", error);
    } finally {
      setLoading(false);
    }
  }, [data?.testId, router, submittedAnswers, timeLeft, timeStorageKey]);

  endTestRef.current = endTest;

  const formatTime = (seconds) => {
    if (typeof seconds !== "number" || Number.isNaN(seconds) || seconds < 0) {
      return "00:00";
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };
  return (
    <Fragment>
      <PageHead title={"title"} />
      <MobileMenu />
      <div className="test-header">
        <button
          className={`btn btn-primary ${
            timeLeft > 900 ? "btn-success" : "btn-danger"
          } fs-3 px-3`}
        >
          {formatTime(timeLeft)}
        </button>
        <button className="btn btn-primary fs-3" onClick={handlePrevQuestion}>
          Prev
        </button>
        <button
          className="btn btn-warning fs-3"
          onClick={() => {
            handleMarkQuestion(selectedQuestion._id);
          }}
        >
          {markedQuestions.includes(selectedQuestion._id) ? "Unmark" : "Mark"}
        </button>
        <button className="btn btn-primary fs-3" onClick={handleNextQuestion}>
          Next
        </button>
        <button className="btn btn-primary fs-3" onClick={toggleNotepad}>
          <i className="feather-edit mx-3" />
        </button>
        <button
          data-toggle="modal"
          data-target="#labValues"
          data-bs-toggle="modal"
          data-bs-target={`#labValues`}
          className="btn btn-primary fs-3"
        >
          <i className="feather-book-open mx-3" />
        </button>
        <button
          data-toggle="modal"
          data-target="#shortCutModal"
          data-bs-toggle="modal"
          data-bs-target={`#shortCutModal`}
          className="btn btn-primary fs-3"
        >
          <i className="feather-command mx-3" />
        </button>
      </div>
      <Cart />
      <div className="rbt-page-banner-wrapper">
        <div className="rbt-banner-image" />
      </div>
      <div className="rbt-dashboard-area rbt-section-overlayping-top rbt-section-gapBottom">
        <LoadAndDisplay isLoading={loading}>
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div className="row g-5">
                  <div className="col-lg-2">
                    <div className="rbt-default-sidebar sticky-top rbt-shadow-box rbt-gradient-border">
                      <div className="inner">
                        <div className="content-item-content">
                          <div className="rbt-default-sidebar-wrapper">
                            <div className="section-title mb--20">
                              <h6 className="rbt-title-style-2">Questions</h6>
                            </div>
                            <nav className="mainmenu-nav">
                              <ul className="dashboard-mainmenu rbt-default-sidebar-list">
                                {data?.questions?.map((data, index) => (
                                  <li
                                    onClick={() => {
                                      setSelectedQuestion({
                                        ...data,
                                        index: index,
                                      });
                                      window.scrollTo({
                                        top: 0,
                                        behavior: "smooth",
                                      });
                                    }}
                                    key={index}
                                  >
                                    <a
                                      className={`${
                                        selectedQuestion._id === data._id
                                          ? "active"
                                          : ""
                                      }`}
                                    >
                                      <i className={data.icon} />
                                      <span>Q. {index + 1}</span>
                                      {markedQuestions.includes(data._id) && (
                                        <FaFlag className="ms-2 text-danger" />
                                      )}
                                      {Object.keys(submittedAnswers).includes(
                                        data._id
                                      ) && (
                                        <FaCircle className="ms-2 text-success" />
                                      )}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            </nav>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={"col-lg-10"}>
                    <div className="rbt-dashboard-content bg-color-white rbt-shadow-box">
                      <div className="content">
                        <h5 className="w-100">
                          Q : {selectedQuestion.index + 1}.{" "}
                          <span className="fs-6 ms-auto">
                            ({selectedQuestion.code})
                          </span>
                        </h5>
                        <p
                          dangerouslySetInnerHTML={{
                            __html: selectedQuestion?.title,
                          }}
                        />
                        <div className="row">
                          {(selectedQuestion?.titleGallery || [])?.map(
                            (image, index) => {
                              return (
                                <div
                                  className="mr-2 mb-2 col"
                                  key={`title-${index}-${image.Location}`}
                                >
                                  <Image
                                    src={image.Location}
                                    height={500}
                                    width={500}
                                    alt=""
                                  />
                                </div>
                              );
                            }
                          )}
                        </div>
                        {/* work here */}
                      </div>
                    </div>

                    <div className="rbt-dashboard-content bg-color-white mt-4 rbt-shadow-box">
                      <div className="">
                        <h5>Choose An Option</h5>
                        {selectedQuestion?.options?.map((option, index) => {
                          const isSubmitted =
                            submittedAnswers[selectedQuestion._id] !==
                            undefined;

                          return (
                            <div key={option.title} className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                name={selectedQuestion.title}
                                id={option.title}
                                checked={
                                  answers[selectedQuestion._id] === index
                                }
                                onChange={() =>
                                  handleOptionChange(
                                    selectedQuestion._id,
                                    index
                                  )
                                }
                                disabled={isSubmitted}
                              />
                              <span className="me-4">
                                {generateLabel(index, "letter")}
                              </span>
                              <label
                                className="form-check-label"
                                htmlFor={option.title}
                                dangerouslySetInnerHTML={{
                                  __html: option.title,
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>

                      <div className="mt--20 d-flex flex-wrap gap-4">
                        <button
                          className="rbt-btn btn-primary btn-sm"
                          onClick={() =>
                            handleSubmitAnswer(selectedQuestion._id)
                          }
                          disabled={
                            submittedAnswers[selectedQuestion._id] !== undefined
                          }
                        >
                          Submit Answer
                        </button>
                        {/* <button className="rbt-btn btn-border btn-xs">
                            {formatTime(timeLeft)}
                          </button> */}
                        <button
                          className="rbt-btn btn-sm bg-danger ms-auto btn-danger"
                          onClick={handleEndTest}
                        >
                          End Test
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </LoadAndDisplay>
      </div>
      <QuestionNotepad
        isOpen={isNotepadOpen}
        onClose={closeNotepad}
        questionId={selectedQuestionId}
        questionLabel={selectedQuestionLabel}
        notes={selectedQuestionId ? notesStore[selectedQuestionId] || [] : []}
        onCreateNote={createNote}
        onUpdateNote={updateNote}
        onDeleteNote={deleteNote}
      />

      <ExtraModals
        handleNextQuestion={handleNextQuestion}
        markedQuestions={markedQuestions}
        handlePrevQuestion={handlePrevQuestion}
        selectedQuestion={selectedQuestion}
        handleMarkQuestion={handleMarkQuestion}
        openModal={openModal}
        timeLeft={formatTime(timeLeft)}
        unansweredCount={unansweredCount}
        handleConfirmEndTest={handleConfirmEndTest}
        openNotepad={openNotepad}
        closeNotepad={closeNotepad}
      />
      <BackToTop />
      <Separator />
    </Fragment>
  );
};

export const getServerSideProps = async (context) => {
  const domainName = context.req ? context.req.headers.host : null;

  const { db } = await connectToDatabase();

  const info = await db.collection("partners").findOne({
    key: process.env.DEFAULT || domainName,
  });

  if (!info) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      info: JSON.stringify(info),
    },
  };
};

export default SubjectSystemSelector;
